import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useTheme } from '@emotion/react';
import { 
  useGetDashboardConfigMasterQuery,
  useApplyDashboardPreferencesMutation,
  useGetUserPreferencesQuery,
} from '../../../store/slices/apiSlice';
import { useDispatch } from 'react-redux';
import { openMessage } from '../../../store/slices/showMessageSlice';
import CustomButton from '../../../components/@extended/CustomButton';

const ReportingDashboardTab = ({ user }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [selectedWidgets, setSelectedWidgets] = useState([]);
  const [error, setError] = useState(null);

  const { data: widgets = [], isLoading: widgetsLoading } = useGetDashboardConfigMasterQuery({});
  const { data: userPreferences = [], isLoading: preferencesLoading } = useGetUserPreferencesQuery(
    { userId: user?.id },
    { skip: !user?.id }
  );
  const [applyDashboardPreferences, { isLoading: saveLoading }] = useApplyDashboardPreferencesMutation();

  // Load existing preferences when user preferences are fetched
  useEffect(() => {
    if (userPreferences && userPreferences.length > 0) {
      const preferenceIds = userPreferences.map((pref) => pref?.preferenceId || pref?.id);
      setSelectedWidgets(preferenceIds);
    }
  }, [userPreferences]);

  const handleWidgetToggle = (widgetId) => {
    setSelectedWidgets((prevSelected) => {
      const currentSelected = [...prevSelected];
      const existingIndex = currentSelected.indexOf(widgetId);

      if (existingIndex !== -1) {
        currentSelected.splice(existingIndex, 1);
      } else {
        currentSelected.push(widgetId);
      }

      return currentSelected;
    });
  };

  const handleSave = async () => {
    setError(null);
    try {
      const payload = {
        userId: user?.id,
        preferenceIds: selectedWidgets,
      };

      const response = await applyDashboardPreferences(payload);
      if (!response.data) throw new Error('Failed to save preferences');

      dispatch(openMessage({ 
        messageSeverity: 'success', 
        message: 'Dashboard preferences saved successfully' 
      }));
    } catch (err) {
      setError(err.message);
      dispatch(openMessage({ 
        messageSeverity: 'error', 
        message: err.message || 'Failed to save preferences' 
      }));
    }
  };

  if (widgetsLoading || preferencesLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Loading dashboard preferences...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, pt: 0 }}>
      <Typography variant="h6" gutterBottom fontWeight={600}>
        Dashboard Widget Preferences
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select widgets to display on the reporting dashboard for this user
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <FormGroup>
        <Grid container spacing={2}>
          {widgets.map((widget) => (
            <Grid item xs={12} sm={6} key={widget.id}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedWidgets.includes(widget.id)}
                    onChange={() => handleWidgetToggle(widget.id)}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" fontWeight={500}>
                      {widget.label}
                    </Typography>
                    <Typography
                      variant="caption"
                      color={theme.palette.text.secondary}
                      sx={{ display: 'block' }}
                    >
                      Type: {widget.type}
                    </Typography>
                  </Box>
                }
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  p: 1,
                  m: 0,
                  width: '100%',
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              />
            </Grid>
          ))}
        </Grid>
      </FormGroup>

      {selectedWidgets.length > 0 && (
        <Box mt={3}>
          <Typography variant="body2" color={theme.palette.primary.main}>
            {selectedWidgets.length} widget(s) selected
          </Typography>
        </Box>
      )}

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <CustomButton
          onClick={handleSave}
          variant="contained"
          disabled={saveLoading}
          startIcon={saveLoading && <CircularProgress size={16} />}
        >
          {saveLoading ? 'Saving...' : 'Save Preferences'}
        </CustomButton>
      </Box>
    </Box>
  );
};

export default ReportingDashboardTab;

